/**
 * SwapForm — amount inputs, quote display, buy/sell toggle, slippage, fee breakdown.
 *
 * Controlled component — state managed by parent (TradePanel).
 * Receives quote data as props rather than fetching internally.
 *
 * Story 5.1 Task 6.1, refactored to controlled in Story 5.4 Task 1
 */

import { useCallback, useState } from "react";

import type { SwapQuote } from "../../hooks/chain/use-swap-quote";
import { formatTokenAmount } from "../../lib/format";
import { getNativeCurrencySymbol } from "../../lib/wagmi-config";
import { SlippageSettings } from "./SlippageSettings";

function QuoteDetails({
	quote,
	payLabel,
	receiveLabel,
	minimumReceivedDisplay,
}: {
	quote: SwapQuote;
	payLabel: string;
	receiveLabel: string;
	minimumReceivedDisplay: string;
}) {
	return (
		<div className="space-y-2 rounded-xl border border-pastel-border bg-white p-3 text-xs">
			<div className="flex items-center justify-between">
				<span className="text-pastel-gray">Price Impact</span>
				<span
					className={`rounded px-1.5 py-0.5 font-medium ${impactColorClass(quote.priceImpact)}`}
				>
					{quote.priceImpact.toFixed(2)}%
				</span>
			</div>
			<div className="flex items-center justify-between">
				<span className="text-pastel-gray">LP Fee</span>
				<span className="text-pastel-black">
					{((quote.feeBreakdown.lpFeePips / 1_000_000) * 100).toFixed(2)}%
				</span>
			</div>
			{quote.feeBreakdown.protocolFeePips > 0 && (
				<div className="flex items-center justify-between">
					<span className="text-pastel-gray">Protocol Fee</span>
					<span className="text-pastel-black">
						{((quote.feeBreakdown.protocolFeePips / 1_000_000) * 100).toFixed(2)}%
					</span>
				</div>
			)}
			<div className="flex items-center justify-between">
				<span className="text-pastel-gray">Rate</span>
				<span className="text-pastel-black">
					1 {payLabel} ≈ {quote.effectiveRate > 0 ? quote.effectiveRate.toFixed(4) : "--"}{" "}
					{receiveLabel}
				</span>
			</div>
			<div className="flex items-center justify-between">
				<span className="text-pastel-gray">Min. Received</span>
				<span className="text-pastel-black">
					{minimumReceivedDisplay || "--"} {receiveLabel}
				</span>
			</div>
		</div>
	);
}

export type SwapFormProps = {
	tokenSymbol: string;
	tokenDecimals?: number;
	direction: "buy" | "sell";
	amountIn: string;
	slippageBps: number;
	onDirectionChange: (direction: "buy" | "sell") => void;
	onAmountInChange: (amount: string) => void;
	onSlippageBpsChange: (bps: number) => void;
	quote: SwapQuote | null;
	isQuoteLoading: boolean;
	quoteError: Error | null;
	noLiquidity?: boolean;
	onRetryQuote: () => void;
};

/**
 * Color class for price impact badge.
 */
function impactColorClass(impact: number): string {
	if (impact < 1) return "text-green-600 bg-green-50";
	if (impact <= 5) return "text-amber-600 bg-amber-50";
	return "text-red-600 bg-red-50";
}

export function SwapForm({
	tokenSymbol,
	tokenDecimals = 18,
	direction,
	amountIn,
	slippageBps,
	onDirectionChange,
	onAmountInChange,
	onSlippageBpsChange,
	quote,
	isQuoteLoading,
	quoteError,
	noLiquidity,
	onRetryQuote,
}: SwapFormProps) {
	const [showSlippage, setShowSlippage] = useState(false);

	const nativeCurrency = getNativeCurrencySymbol();

	// Token labels based on direction
	const payLabel = direction === "buy" ? nativeCurrency : tokenSymbol;
	const receiveLabel = direction === "buy" ? tokenSymbol : nativeCurrency;

	// Output decimals: buying → token decimals; selling → ETH (18)
	const outputDecimals = direction === "buy" ? tokenDecimals : 18;

	// Format output amount for display (truncated for readability)
	const outputDisplay =
		quote?.amountOut !== undefined ? formatTokenAmount(quote.amountOut, outputDecimals) : "";

	const minimumReceivedDisplay =
		quote?.minimumReceived !== undefined
			? formatTokenAmount(quote.minimumReceived, outputDecimals)
			: "";

	// Show no-liquidity banner only when user entered an amount and quoter returned nothing
	const hasAmount = amountIn !== "" && amountIn !== "0";
	const showNoLiquidity = noLiquidity && !quoteError && !quote && hasAmount;

	const handleDirectionToggle = useCallback(
		(newDirection: "buy" | "sell") => {
			onDirectionChange(newDirection);
			onAmountInChange("");
		},
		[onDirectionChange, onAmountInChange]
	);

	return (
		<div className="space-y-4">
			{/* Buy/Sell Toggle */}
			<div className="flex rounded-lg bg-pastel-bg p-1">
				<button
					type="button"
					aria-label="Buy"
					onClick={() => handleDirectionToggle("buy")}
					className={`flex-1 rounded-md py-2 text-sm font-semibold transition-colors ${
						direction === "buy"
							? "bg-green-600 text-white"
							: "text-pastel-gray hover:text-pastel-black"
					}`}
				>
					Buy
				</button>
				<button
					type="button"
					aria-label="Sell"
					onClick={() => handleDirectionToggle("sell")}
					className={`flex-1 rounded-md py-2 text-sm font-semibold transition-colors ${
						direction === "sell"
							? "bg-red-500 text-white"
							: "text-pastel-gray hover:text-pastel-black"
					}`}
				>
					Sell
				</button>
			</div>

			{/* You Pay */}
			<div className="rounded-xl border border-pastel-border bg-white p-4">
				<div className="flex items-center justify-between mb-2">
					<span className="text-xs text-pastel-gray font-medium">You Pay</span>
					<span className="text-xs text-pastel-gray">{payLabel}</span>
				</div>
				<input
					type="text"
					inputMode="decimal"
					placeholder="0.0"
					value={amountIn}
					onChange={(e) => {
						const val = e.target.value;
						if (val === "" || /^\d*\.?\d*$/.test(val)) {
							onAmountInChange(val);
						}
					}}
					className="w-full bg-transparent text-2xl font-semibold text-pastel-black outline-none placeholder:text-pastel-border"
				/>
			</div>

			{/* You Receive */}
			<div className="rounded-xl border border-pastel-border bg-pastel-bg p-4">
				<div className="flex items-center justify-between mb-2">
					<span className="text-xs text-pastel-gray font-medium">You Receive</span>
					<span className="text-xs text-pastel-gray">{receiveLabel}</span>
				</div>
				{isQuoteLoading ? (
					<div
						data-testid="quote-loading"
						className="h-8 w-32 animate-pulse rounded bg-pastel-border"
					/>
				) : (
					<div className="text-2xl font-semibold text-pastel-black">
						{outputDisplay || <span className="text-pastel-border">0.0</span>}
					</div>
				)}
			</div>

			{/* Error State */}
			{quoteError && (
				<div className="rounded-xl border border-red-200 bg-red-50 p-4 text-center">
					<p className="text-sm text-red-700 mb-2">Unable to fetch quote — chain unavailable</p>
					<button
						type="button"
						aria-label="Retry"
						onClick={onRetryQuote}
						className="rounded-lg bg-red-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-red-700 transition-colors"
					>
						Retry
					</button>
				</div>
			)}

			{/* No Liquidity — only show after user entered an amount and quoter returned no result */}
			{showNoLiquidity && (
				<div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-center">
					<p className="text-sm text-amber-800">
						This pool has no liquidity yet. Trading will be available once liquidity is added.
					</p>
				</div>
			)}

			{/* Quote Details */}
			{quote && !quoteError && (
				<QuoteDetails
					quote={quote}
					payLabel={payLabel}
					receiveLabel={receiveLabel}
					minimumReceivedDisplay={minimumReceivedDisplay}
				/>
			)}

			{/* Slippage Settings */}
			<div>
				<button
					type="button"
					onClick={() => setShowSlippage(!showSlippage)}
					className="flex items-center gap-1 text-xs text-pastel-gray hover:text-pastel-black transition-colors"
				>
					<svg
						className="h-3.5 w-3.5"
						fill="none"
						viewBox="0 0 24 24"
						strokeWidth={1.5}
						stroke="currentColor"
						aria-hidden="true"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z"
						/>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
						/>
					</svg>
					Slippage ({(slippageBps / 100).toFixed(1)}%)
				</button>
				{showSlippage && (
					<div className="mt-2">
						<SlippageSettings slippageBps={slippageBps} onSlippageChange={onSlippageBpsChange} />
					</div>
				)}
			</div>
		</div>
	);
}
